import { useState } from 'react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Field,
  Flex,
  Grid,
  Heading,
  Input,
  NativeSelect,
  Separator,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { LuCalendar, LuClock, LuInfo, LuPencil, LuX } from 'react-icons/lu'
import PhoneInput from '@/components/PhoneInput'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useUpdateEvent } from '@/hooks/events/useUpdateEvent'
import { EVENT_STATUS_CONFIG } from '@/utils/events'
import { firstAndLastName } from '@/utils/string'
import type { ApiErrorResponse } from '@/lib/api'
import type { EventDetail } from '@/services/events'

type Props = {
  event: EventDetail
  agendaId: string
  canEditEvents: boolean
  canEditNotes: boolean
}

const GeneralSection = ({ event, agendaId, canEditEvents, canEditNotes }: Props) => {
  const [isEditing, setIsEditing] = useState(false)

  const mutation = useUpdateEvent(agendaId!, event.id)
  const validationErrors = (mutation.error as AxiosError<ApiErrorResponse> | null)?.response?.data
    ?.validation_errors

  const handleCancel = () => {
    mutation.reset()
    setIsEditing(false)
  }

  const handleSubmit = (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)

    const payload = {
      name: (data.get('name') as string).trim(),
      notes: (data.get('notes') as string).trim() || null,
      event_date: data.get('event_date') as string,
      event_time: (data.get('event_time') as string).trim() || null,
      status: Number(data.get('status')),
      contractor_name: (data.get('contractor_name') as string).trim() || null,
      contractor_phone: (data.get('contractor_phone') as string).trim() || null,
    }

    mutation.mutate(payload, {
      onSuccess: () => {
        showSuccess('Evento atualizado com sucesso!')
        setIsEditing(false)
      },
    })
  }

  const statusConfig = EVENT_STATUS_CONFIG[event.status] ?? { label: event.status, colorPalette: 'gray' }

  const statusToInt: Record<string, number> = { prospection: 0, confirmed: 1, cancelled: 2, searched: 3 }

  return (
    <Card.Root h="full">
      <Card.Body display="flex" flexDirection="column" gap={4}>
        <Flex justify="space-between" align="flex-start">
          <Flex align="center" gap={3}>
            <Box
              bg="blue.subtle"
              color="blue.600"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <LuInfo size={16} />
            </Box>
            <Box>
              <Heading size="sm" fontWeight="semibold">
                Informações Básicas
              </Heading>
              <Text fontSize="xs" color="fg.muted">
                Dados principais e metadados do evento
              </Text>
            </Box>
          </Flex>
          {!isEditing && canEditEvents && (
            <Button size="xs" variant="ghost" onClick={() => setIsEditing(true)} aria-label="Editar">
              <LuPencil />
            </Button>
          )}
        </Flex>

        {isEditing ? (
          <form onSubmit={handleSubmit}>
            <Stack gap={4}>
              <Field.Root required invalid={!!validationErrors?.name}>
                <Field.Label>
                  Nome do Evento
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input
                  name="name"
                  placeholder="Ex: Festival de Verão 2024"
                  variant="subtle"
                  maxLength={150}
                  defaultValue={event.name}
                />
                {validationErrors?.name?.map((error, i) => (
                  <Field.ErrorText key={i}>{error}</Field.ErrorText>
                ))}
              </Field.Root>

              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <Field.Root required invalid={!!validationErrors?.event_date}>
                  <Field.Label>
                    Data do Evento
                    <Field.RequiredIndicator />
                  </Field.Label>
                  <Input
                    name="event_date"
                    type="date"
                    variant="subtle"
                    defaultValue={event.event_date ?? ''}
                  />
                  {validationErrors?.event_date?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>

                <Field.Root invalid={!!validationErrors?.event_time}>
                  <Field.Label>Horário</Field.Label>
                  <Input
                    name="event_time"
                    type="time"
                    variant="subtle"
                    defaultValue={event.event_time ?? ''}
                  />
                  {validationErrors?.event_time?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>
              </Grid>

              <Field.Root required invalid={!!validationErrors?.status}>
                <Field.Label>
                  Status
                  <Field.RequiredIndicator />
                </Field.Label>
                <NativeSelect.Root variant="subtle">
                  <NativeSelect.Field name="status" defaultValue={statusToInt[event.status] ?? 0}>
                    <option value={0}>Reservado</option>
                    <option value={1}>Confirmado</option>
                    <option value={2}>Cancelado</option>
                    <option value={3}>Procurado</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                {validationErrors?.status?.map((error, i) => (
                  <Field.ErrorText key={i}>{error}</Field.ErrorText>
                ))}
              </Field.Root>

              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                <Field.Root invalid={!!validationErrors?.contractor_name}>
                  <Field.Label>Nome do contratante</Field.Label>
                  <Input
                    name="contractor_name"
                    placeholder="Ex: João Silva"
                    variant="subtle"
                    maxLength={150}
                    defaultValue={event.contractor_name ?? ''}
                  />
                  {validationErrors?.contractor_name?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>

                <Field.Root invalid={!!validationErrors?.contractor_phone}>
                  <Field.Label>Telefone do contratante</Field.Label>
                  <PhoneInput
                    name="contractor_phone"
                    variant="subtle"
                    defaultValue={event.contractor_phone ?? ''}
                  />
                  {validationErrors?.contractor_phone?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>
              </Grid>

              {canEditNotes && (
                <Field.Root invalid={!!validationErrors?.notes}>
                  <Field.Label>Notas Internas</Field.Label>
                  <Textarea
                    name="notes"
                    placeholder="Observações sobre o evento (opcional)"
                    variant="subtle"
                    rows={3}
                    maxLength={500}
                    defaultValue={event.notes ?? ''}
                  />
                  {validationErrors?.notes?.map((error, i) => (
                    <Field.ErrorText key={i}>{error}</Field.ErrorText>
                  ))}
                </Field.Root>
              )}

              <Flex gap={2} justify="flex-end">
                <Button variant="outline" size="sm" onClick={handleCancel} disabled={mutation.isPending}>
                  <LuX />
                  Cancelar
                </Button>
                <Button type="submit" colorPalette="primary" size="sm" loading={mutation.isPending}>
                  Salvar
                </Button>
              </Flex>
            </Stack>
          </form>
        ) : (
          <>
            <Flex direction="column" gap={4}>
              <Box>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                  Nome do Evento
                </Text>
                <Text fontWeight="semibold" fontSize="lg">
                  {event.name}
                </Text>
              </Box>

              <Flex direction={{ base: 'column', md: 'row' }} gap={4} justifyContent="space-between">
                <Box>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                    Agenda
                  </Text>
                  <Stack gap={1}>
                    {event.event_date ? (
                      <Flex align="center" gap={1} fontSize="md" fontWeight="semibold">
                        <LuCalendar size={13} />
                        <Text>
                          {format(new Date(event.event_date + 'T00:00:00'), "dd 'de' MMM. yyyy", { locale: ptBR })}
                        </Text>
                      </Flex>
                    ) : (
                      <Text fontSize="sm" color="fg.muted">—</Text>
                    )}
                    {event.event_time && (
                      <Flex align="center" gap={1} fontSize="md" fontWeight="semibold">
                        <LuClock size={13} />
                        <Text>{event.event_time ? event.event_time.slice(0, 5) + 'h' : '—'}</Text>
                      </Flex>
                    )}
                  </Stack>
                </Box>
                <Box>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                    Status
                  </Text>
                  <Badge colorPalette={statusConfig.colorPalette} variant="subtle" size="md">
                    {statusConfig.label}
                  </Badge>
                </Box>
              </Flex>
            </Flex>

            {event.notes && (
              <Box>
                <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={2}>
                  Notas Internas
                </Text>
                <Box
                  bg="bg.subtle"
                  borderLeft="3px solid"
                  borderColor="blue.200"
                  borderRadius="md"
                  px={4}
                  py={3}
                >
                  <Text fontSize="sm" color="fg.muted" fontStyle="italic">
                    "{event.notes}"
                  </Text>
                </Box>
              </Box>
            )}

            {(event.contractor_name || event.contractor_phone) && (
              <Box>
                <Separator mb={3} />
                <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={3}>
                  Dados do contratante
                </Text>
                <Grid templateColumns="1fr 1fr" gap={4}>
                  {event.contractor_name && (
                    <Box>
                      <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                        Nome
                      </Text>
                      <Text fontSize="sm">{event.contractor_name}</Text>
                    </Box>
                  )}
                  {event.contractor_phone && (
                    <Box>
                      <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                        Telefone
                      </Text>
                      <Text fontSize="sm">{event.contractor_phone}</Text>
                    </Box>
                  )}
                </Grid>
              </Box>
            )}

            <Box mt="auto">
              <Separator mb={3} />
              <Grid templateColumns="1fr 1fr" gap={4}>
                <Box>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                    Criado por
                  </Text>
                  <Flex align="center" gap={2}>
                    <Avatar.Root size="2xs">
                      <Avatar.Image src={event.creator.avatar_url ?? undefined} />
                      <Avatar.Fallback>{event.creator.name.charAt(0).toUpperCase()}</Avatar.Fallback>
                    </Avatar.Root>
                    <Text fontSize="sm">{firstAndLastName(event.creator.name)}</Text>
                  </Flex>
                </Box>
                <Box>
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide" mb={1}>
                    Criado em
                  </Text>
                  <Text fontSize="sm">
                    {format(new Date(event.created_at), "dd MMM. yyyy '•' HH:mm", { locale: ptBR })}
                  </Text>
                </Box>
              </Grid>
            </Box>
          </>
        )}
      </Card.Body>
    </Card.Root>
  )
}

export default GeneralSection
